import { z } from 'zod';

/**
 * Client-side email sendability check for the waitlist form — the instant,
 * offline complement to the backend `email.IsSendable` (Go). It catches obvious
 * junk before the server round-trip; the server remains the gate.
 *
 * NOTE: keep BLOCKED_DOMAINS / SYNTHETIC_SUFFIX in sync with
 * internal/email/validate.go in the discovr backend.
 */
const BLOCKED_DOMAINS = new Set(['example.com', 'fakemail.com', 'discovrtest.io', 'resend.dev']);
const SYNTHETIC_SUFFIX = '.discovr';

const emailSchema = z.email();

export type EmailFormatReason = 'invalid_format' | 'synthetic' | 'blocked_domain';
export type EmailFormatResult = { ok: true } | { ok: false; reason: EmailFormatReason };

/** validateEmailFormat runs offline format + blocklist checks. */
export function validateEmailFormat(raw: string): EmailFormatResult {
  const email = raw.trim().toLowerCase();
  if (!emailSchema.safeParse(email).success) {
    return { ok: false, reason: 'invalid_format' };
  }
  const domain = email.slice(email.lastIndexOf('@') + 1);
  if (!domain.includes('.')) {
    return { ok: false, reason: 'invalid_format' };
  }
  if (domain.endsWith(SYNTHETIC_SUFFIX)) {
    return { ok: false, reason: 'synthetic' };
  }
  if (BLOCKED_DOMAINS.has(domain)) {
    return { ok: false, reason: 'blocked_domain' };
  }
  return { ok: true };
}

/**
 * emailErrorMessage maps a reason code (client or server) to inline copy.
 * Returns '' for `ok` and `inconclusive` — neither should block or alarm.
 */
export function emailErrorMessage(reason: string): string {
  switch (reason) {
    case 'invalid_format':
      return 'Enter a valid email address';
    case 'synthetic':
    case 'blocked_domain':
      return 'Please use a valid work email';
    case 'no_mail_domain':
      return "We couldn't find a mail server for that domain — check the spelling";
    default:
      return '';
  }
}
